import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import { Button, Tooltip } from "@mui/material";
import { type Issue, SubmissionDocumentType } from "generated-graphql/graphql";
import { type SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import { useMemo } from "react";

import { type SubmissionDocumentMetadata } from "./helpers/documentHelpers";

interface SaveAndPrepareForSigningButtonProps {
  signer: SubmissionReviewerSigner | null;
  documents: SubmissionDocumentMetadata[];
  issues: Issue[];
  loading: boolean;
  formLoading: boolean;
  isSubmitting: boolean;
  isValidating: boolean;
  onClick: () => void;
}

export function SaveAndPrepareForSigningButton({
  signer,
  documents,
  issues,
  loading,
  formLoading,
  isSubmitting,
  isValidating,
  onClick,
}: SaveAndPrepareForSigningButtonProps) {
  const signerDueDateInPast = useMemo(() => {
    if (!signer?.dueDate) return false;
    const dueDateUtc = DateTime.utc(signer.dueDate.year, signer.dueDate.month, signer.dueDate.day);
    const tomorrowUtc = DateTime.utc().startOf("day").plus({ days: 1 });
    return dueDateUtc < tomorrowUtc;
  }, [signer?.dueDate]);

  const prepareForSigningDisabled = useMemo(() => {
    if (!signer) {
      return true;
    }

    if (signerDueDateInPast) {
      return true;
    }

    if (issues.length > 0) {
      return true;
    }

    if (
      documents.filter((d) => d.submissionDocumentType === SubmissionDocumentType.Submission)
        .length === 0
    ) {
      return true;
    }

    return loading || formLoading || isSubmitting || isValidating;
  }, [
    documents,
    issues,
    formLoading,
    isSubmitting,
    isValidating,
    loading,
    signer,
    signerDueDateInPast,
  ]);

  const tooltipTitle = useMemo(() => {
    if (!signer) {
      return "Signing preparation requires a Signer and Submission Document";
    }
    if (signerDueDateInPast) {
      const earliest = DateTime.utc().plus({ days: 1 }).toFormat("MMM d, yyyy");
      return `Signer due date must be ${earliest} or later (based on UTC)`;
    }
    if (prepareForSigningDisabled) {
      return "Signing preparation requires a Signer and Submission Document";
    }
    return "";
  }, [signer, signerDueDateInPast, prepareForSigningDisabled]);

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <Button
          variant="contained"
          endIcon={<DrawOutlinedIcon />}
          disabled={prepareForSigningDisabled}
          onClick={onClick}
          sx={{ textTransform: "uppercase" }}
        >
          SAVE & PREPARE FOR SIGNING
        </Button>
      </span>
    </Tooltip>
  );
}
